import React, { createContext, useContext, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { authAPI } from '@/lib/api';
import { AuthUser, LoginCredentials, RegisterData } from '@/types';
import toast from 'react-hot-toast';
interface AuthContextType {
  user: AuthUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (credentials: LoginCredentials) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  logout: () => Promise<void>;
  logoutAll: () => Promise<void>;
  updateUser: (user: AuthUser) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [hasToken, setHasToken] = useState<boolean>(() => !!localStorage.getItem('accessToken'));
  const queryClient = useQueryClient();

  // The query cache IS the source of truth for the current user. Deriving `user`
  // straight from it (rather than mirroring it into state via an effect) matters:
  // an effect lands one render late, and during that render isLoading is already
  // false while user is still null — long enough for ProtectedRoute to treat a
  // signed-in user as signed out and bounce a deep link to /login.
  const { data: user = null, isPending } = useQuery<AuthUser | null>({
    queryKey: ['auth', 'me'],
    queryFn: async () => {
      try {
        const response = await authAPI.getMe();
        return (response.data as any).user;
      } catch (error: any) {
        // If 401, user is not authenticated
        if (error.response?.status === 401) {
          localStorage.removeItem('accessToken');
          setHasToken(false);
          return null;
        }
        throw error;
      }
    },
    enabled: hasToken, // Only run query if token exists
    retry: (failureCount, error: any) => {
      // Don't retry on 401 errors
      if (error?.response?.status === 401) {
        return false;
      }
      return failureCount < 2;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false, // Don't refetch on window focus
    refetchOnMount: false, // Don't refetch on mount if data exists
  });

  // A stored token that hasn't been verified yet still counts as "resolving", so
  // route guards wait instead of deciding on an incomplete answer. Without the
  // hasToken guard this would never settle: a disabled query stays `pending`.
  const isResolving = hasToken && isPending;

  const login = async (credentials: LoginCredentials) => {
    const response = await authAPI.login(credentials);
    if (response.success) {
      const { accessToken, user: loggedInUser } = response.data as any;
      localStorage.setItem('accessToken', accessToken);
      setHasToken(true);
      queryClient.setQueryData(['auth', 'me'], loggedInUser);
    }
  };

  const register = async (data: RegisterData) => {
    const response = await authAPI.register(data);
    if (response.success) {
      const { accessToken, user: registeredUser } = response.data as any;
      localStorage.setItem('accessToken', accessToken);
      setHasToken(true);
      queryClient.setQueryData(['auth', 'me'], registeredUser);
    }
  };

  const logout = async () => {
    try {
      await authAPI.logout();
    } catch (error) {
      // Continue with logout even if API call fails
      console.error('Logout API call failed:', error);
    } finally {
      // Clear local state and storage
      setHasToken(false);
      localStorage.removeItem('accessToken');

      // Clear all queries
      queryClient.clear();

      toast.success('Logged out successfully');
    }
  };

  const logoutAll = async () => {
    try {
      await authAPI.logoutAll();
    } catch (error) {
      // Continue with logout even if API call fails
      console.error('Logout all API call failed:', error);
    } finally {
      // Clear local state and storage
      setHasToken(false);
      localStorage.removeItem('accessToken');

      // Clear all queries
      queryClient.clear();

      toast.success('Logged out from all devices');
    }
  };

  const updateUser = (updatedUser: AuthUser) => {
    // Writing the bare user (not { user }) keeps the cache in the same shape the
    // query function returns, which is what `user` above reads from.
    queryClient.setQueryData(['auth', 'me'], updatedUser);
  };

  const value: AuthContextType = {
    user,
    isLoading: isResolving,
    isAuthenticated: !!user,
    login,
    register,
    logout,
    logoutAll,
    updateUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};